/**
 * 로케일 «결정» 모듈 — 번역 카탈로그(en.json · ko.json)를 끌어오지 않는다.
 *
 * TRAD-3662 — 왜 `index.ts` 에서 떼어냈나: shared/api 의 요청 인터셉터가 현재 로케일을 읽어
 * `Accept-Language` 로 보내야 하는데, shared/api 배럴은 SharedWorker 등 window 없는 컨텍스트로도
 * 끌려간다(`error-localize.ts` 머리말 — «t() 를 두는 파일은 shared/api 배럴에 export 하지 않는다»).
 * 카탈로그 json 두 개까지 딸려가지 않게 **저장소 읽기·쓰기와 협상 규칙만** 여기 두고,
 * `@/i18n` 은 이것들을 그대로 재export 한다(기존 import 경로 불변).
 *
 * 협상 규칙(순수 함수 `resolveLocale`):
 *   ① localStorage('locale') — 사용자가 언어 토글로 명시 선택한 값 (브라우저 언어를 이긴다)
 *   ② navigator.language 의 언어 접두사
 *   ③ 'ko' 폴백 — 둘 다 지원 로케일이 아닐 때
 */

/** 사용자가 명시 선택한 로케일을 담는 localStorage 키. */
export const LOCALE_STORAGE_KEY = 'locale';

/** 선택 UI 가 노출하는 지원 로케일 목록 (카탈로그와 1:1). */
export const SUPPORTED_LOCALES = ['ko', 'en'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: SupportedLocale = 'ko';

/**
 * 지원 로케일 판정. ~~`stored in TRANSLATIONS`~~ 는 프로토타입 키(`'constructor'`·`'toString'`)도
 * 통과시켰다 — 그 값이 저장돼 있으면 t() 가 함수 객체를 카탈로그로 읽어 죽는다. 목록 포함 여부로만 본다.
 */
export const isSupportedLocale = (value: string | null | undefined): value is SupportedLocale =>
  value != null && (SUPPORTED_LOCALES as readonly string[]).includes(value);

/** 순수 함수 — 저장값 → 브라우저 언어 접두사 → 'ko'. 같은 입력이면 같은 출력. */
export const resolveLocale = (
  stored: string | null | undefined,
  navigatorLanguage: string | undefined,
): SupportedLocale => {
  if (isSupportedLocale(stored)) return stored;
  const prefix = (navigatorLanguage ?? '').split('-')[0];
  return isSupportedLocale(prefix) ? prefix : DEFAULT_LOCALE;
};

/**
 * 스토리지 실패는 "한 번만" 로깅한다.
 * t() 는 렌더마다 문자열 개수만큼 호출되므로(화면당 수십~수백 회), 매번 로깅하면
 * 프라이빗 모드 사용자의 콘솔이 같은 에러로 도배돼 정작 봐야 할 로그가 묻힌다.
 * raw error 는 삼키지 않되(CLAUDE.md), 소음이 되지 않게 1회로 제한.
 */
let storageErrorLogged = false;

function logStorageErrorOnce(action: 'read' | 'write', e: unknown): void {
  if (storageErrorLogged) return;
  storageErrorLogged = true;
  console.error(`[i18n] localStorage ${action} failed — 로케일 선택이 유지되지 않습니다`, e);
}

/** 테스트에서 1회 로깅 가드를 초기화하기 위한 훅. 프로덕션 경로에서는 호출하지 않는다. */
export function __resetStorageErrorLogForTest(): void {
  storageErrorLogged = false;
}

/**
 * 사용 가능한 Storage 를 반환한다. 없거나 규격에 맞지 않으면 null.
 *
 * 규격 확인(`typeof getItem === 'function'`)이 필요한 이유: 일부 런타임은 전역 `localStorage`
 * 를 Storage 가 아닌 껍데기 객체로 정의해 둔다(Node 의 실험적 webstorage 전역 등).
 * 이때 곧바로 getItem 을 부르면 TypeError 가 나므로, 예외가 아니라 "미지원"으로 조용히 폴백한다.
 */
function getStorage(): Storage | null {
  try {
    if (typeof localStorage === 'undefined' || localStorage === null) return null;
    if (typeof localStorage.getItem !== 'function' || typeof localStorage.setItem !== 'function') {
      return null;
    }
    return localStorage;
  } catch (e) {
    // 프라이빗 모드/스토리지 차단 환경에선 접근 자체가 throw 한다.
    logStorageErrorOnce('read', e);
    return null;
  }
}

/**
 * localStorage 에 저장된 "사용자가 직접 고른" 로케일을 읽는다.
 * 접근이 throw 할 수 있으므로 반드시 감싼다 — 여기서 터지면 t() 가 통째로 죽어 전 화면이 빈다.
 */
export function readStoredLocale(): SupportedLocale | null {
  const storage = getStorage();
  if (!storage) return null;

  try {
    const stored = storage.getItem(LOCALE_STORAGE_KEY);
    return isSupportedLocale(stored) ? stored : null;
  } catch (e) {
    logStorageErrorOnce('read', e);
    return null;
  }
}

/**
 * 사용자 선택 로케일을 저장한다. 저장 성공 여부를 반환(실패 시 호출부가 reload 를 건너뛰도록).
 * 사이드 이펙트(스토리지 쓰기)만 담당 — 문서 lang 동기화·reload 는 호출부(`setLocale`)가 결정한다.
 */
export function writeStoredLocale(locale: SupportedLocale): boolean {
  const storage = getStorage();
  if (!storage) return false;

  try {
    storage.setItem(LOCALE_STORAGE_KEY, locale);
    return true;
  } catch (e) {
    logStorageErrorOnce('write', e);
    return false;
  }
}

/**
 * 표시 로케일 결정(저장값 → navigator.language → 'ko'). t() 가 매 호출마다 이 함수를 부르므로,
 * 저장값을 바꾸면 다음 렌더부터 반영된다(전 화면 일관 반영을 위해 선택 시에는 setLocale() 이 reload 를 수행).
 * TRAD-3662 부터는 shared/api 요청 인터셉터도 매 요청마다 이 값을 `Accept-Language` 로 싣는다.
 * 반환형은 종전대로 `string` — 호출부 테스트들이 «미지원 로케일(ja)이 왔을 때» 방어 경로를 mock 으로 고정하고 있다.
 */
export function getLocale(): string {
  return resolveLocale(readStoredLocale(), typeof navigator !== 'undefined' ? navigator.language : undefined);
}
